import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, RefreshCw, Trash2, Mail, Phone, MapPin, Calendar, Clock, FileText } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../LangContext";
import api, { formatApiError } from "../api";
import { LOGO_URL } from "../constants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  contacted: "bg-blue-100 text-blue-800 border-blue-200",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-[#7a8b50]/20 text-[#5f6e3d] border-[#7a8b50]/30",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
};

const StatusBadge = ({ status }) => {
  const { t } = useLang();
  return (
    <span
      data-testid={`status-${status}`}
      className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${STATUS_COLORS[status] || ""}`}
    >
      {t.status[status] || status}
    </span>
  );
};

export default function AdminDashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ quotes_total: 0, bookings_total: 0, quotes_pending: 0, bookings_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, b, s] = await Promise.all([
        api.get("/quotes"),
        api.get("/bookings"),
        api.get("/admin/stats"),
      ]);
      setQuotes(q.data);
      setBookings(b.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login");
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("rc_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    try {
      const u = JSON.parse(localStorage.getItem("rc_user") || "null");
      setUser(u);
    } catch (e) {
      // ignore JSON parse errors
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("rc_token");
    localStorage.removeItem("rc_user");
    navigate("/admin/login");
  };

  const updateQuoteStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/quotes/${id}/status`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
      toast.success("Actualizado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}/status`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
      toast.success("Actualizado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteQuote = async (id) => {
    try {
      await api.delete(`/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteBooking = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF5]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-[#1e3a5f]/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Ruizcare" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-serif-display text-xl text-[#1e3a5f] font-semibold">Ruizcare</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#7a8b50] font-semibold">
                {t.admin.dashboard}
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#1e3a5f]/70 hover:text-[#7a8b50]"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refrescar
            </button>
            {user && <span className="hidden md:inline text-sm text-[#1e3a5f]/70">{user.email}</span>}
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#1e3a5f]/20 text-xs uppercase tracking-widest text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-[#FAFAF5] transition-colors font-semibold"
            >
              <LogOut className="h-3.5 w-3.5" /> {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
          <StatCard label={t.admin.stats.quotes} value={stats.quotes_total} testId="stat-quotes" />
          <StatCard label={t.admin.stats.bookings} value={stats.bookings_total} testId="stat-bookings" />
          <StatCard label={`${t.admin.stats.quotes} · ${t.admin.stats.pending}`} value={stats.quotes_pending} testId="stat-pending-quotes" highlight />
          <StatCard label={`${t.admin.stats.bookings} · ${t.admin.stats.pending}`} value={stats.bookings_pending} testId="stat-pending-bookings" highlight />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-7 border-b border-[#1e3a5f]/10">
          <TabButton testId="tab-quotes" active={tab === "quotes"} onClick={() => setTab("quotes")}>
            {t.admin.tabs.quotes}
          </TabButton>
          <TabButton testId="tab-bookings" active={tab === "bookings"} onClick={() => setTab("bookings")}>
            {t.admin.tabs.bookings}
          </TabButton>
        </div>

        {loading ? (
          <div className="text-center text-[#1e3a5f]/60 py-16">···</div>
        ) : tab === "quotes" ? (
          <QuotesList
            items={quotes}
            onStatusChange={updateQuoteStatus}
            onDelete={deleteQuote}
            empty={t.admin.empty}
            t={t}
          />
        ) : (
          <BookingsList
            items={bookings}
            onStatusChange={updateBookingStatus}
            onDelete={deleteBooking}
            empty={t.admin.empty}
            t={t}
          />
        )}
      </main>
    </div>
  );
}

const StatCard = ({ label, value, testId, highlight }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl p-5 border ${highlight ? "bg-[#1e3a5f] border-[#1e3a5f] text-[#FAFAF5]" : "bg-white border-[#7a8b50]/20 text-[#1e3a5f]"}`}
  >
    <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 ${highlight ? "text-[#FAFAF5]/70" : "text-[#7a8b50]"}`}>
      {label}
    </div>
    <div className={`font-serif-display text-4xl font-semibold leading-none`}>{value}</div>
  </div>
);

const TabButton = ({ active, onClick, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
      active
        ? "border-[#7a8b50] text-[#1e3a5f]"
        : "border-transparent text-[#1e3a5f]/50 hover:text-[#1e3a5f]"
    }`}
  >
    {children}
  </button>
);

const StatusSelect = ({ value, onChange, options, testId }) => {
  const { t } = useLang();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-9 text-xs w-[150px] bg-white border-[#1e3a5f]/20">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((s) => (
          <SelectItem key={s} value={s}>
            {t.status[s] || s}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const QuotesList = ({ items, onStatusChange, onDelete, empty, t }) => {
  if (!items.length)
    return <div className="text-center text-[#1e3a5f]/50 py-16" data-testid="quotes-empty">{empty}</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="quotes-list">
      {items.map((q) => (
        <div
          key={q.id}
          data-testid={`quote-row-${q.id}`}
          className="bg-white rounded-3xl p-6 border border-[#7a8b50]/20 shadow-sm"
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="font-serif-display text-xl font-semibold text-[#1e3a5f]">{q.name}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#7a8b50] font-semibold mt-1">
                {t.serviceKeys[q.service] || q.service}
              </div>
            </div>
            <StatusBadge status={q.status} />
          </div>

          <div className="text-sm text-[#1e3a5f]/80 space-y-2 mb-4">
            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#7a8b50]" />{q.email}</div>
            <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#7a8b50]" />{q.phone}</div>
            <div className="flex items-start gap-2"><FileText className="h-4 w-4 text-[#7a8b50] mt-0.5" /><span className="text-[#1e3a5f]/75">{q.message}</span></div>
          </div>

          <div className="text-[11px] text-[#1e3a5f]/40 mb-4">
            {new Date(q.created_at).toLocaleString()}
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-[#1e3a5f]/10">
            <StatusSelect
              testId={`quote-status-${q.id}`}
              value={q.status}
              onChange={(v) => onStatusChange(q.id, v)}
              options={["pending", "contacted", "completed", "cancelled"]}
            />
            <button
              data-testid={`quote-delete-${q.id}`}
              onClick={() => onDelete(q.id)}
              className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800"
            >
              <Trash2 className="h-3.5 w-3.5" /> {t.admin.delete}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const BookingsList = ({ items, onStatusChange, onDelete, empty, t }) => {
  if (!items.length)
    return <div className="text-center text-[#1e3a5f]/50 py-16" data-testid="bookings-empty">{empty}</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="bookings-list">
      {items.map((b) => (
        <div
          key={b.id}
          data-testid={`booking-row-${b.id}`}
          className="bg-white rounded-3xl p-6 border border-[#7a8b50]/20 shadow-sm"
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="font-serif-display text-xl font-semibold text-[#1e3a5f]">{b.name}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#7a8b50] font-semibold mt-1">
                {t.serviceKeys[b.service] || b.service}
              </div>
            </div>
            <StatusBadge status={b.status} />
          </div>

          <div className="text-sm text-[#1e3a5f]/80 grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#7a8b50]" />{b.date}</div>
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#7a8b50]" />{b.time}</div>
            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#7a8b50]" />{b.email}</div>
            <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#7a8b50]" />{b.phone}</div>
            <div className="flex items-start gap-2 sm:col-span-2"><MapPin className="h-4 w-4 text-[#7a8b50] mt-0.5" /><span>{b.address}</span></div>
            {b.notes && <div className="flex items-start gap-2 sm:col-span-2"><FileText className="h-4 w-4 text-[#7a8b50] mt-0.5" /><span className="text-[#1e3a5f]/75">{b.notes}</span></div>}
          </div>

          <div className="text-[11px] text-[#1e3a5f]/40 mb-4">
            {new Date(b.created_at).toLocaleString()}
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-[#1e3a5f]/10">
            <StatusSelect
              testId={`booking-status-${b.id}`}
              value={b.status}
              onChange={(v) => onStatusChange(b.id, v)}
              options={["pending", "confirmed", "completed", "cancelled"]}
            />
            <button
              data-testid={`booking-delete-${b.id}`}
              onClick={() => onDelete(b.id)}
              className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800"
            >
              <Trash2 className="h-3.5 w-3.5" /> {t.admin.delete}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
